import { useState, useEffect } from "react";
import clsx from "clsx";

import { useAtom } from "../../app-jotai";
import { userDialogStateAtom } from "../../auth/authAtoms";
import { useHandleAppTheme } from "../../useHandleAppTheme";

import "./CreateUserModal.scss";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api";

interface CreateUserModalProps {
  onUserCreated: () => void;
}

export const CreateUserModal = ({ onUserCreated }: CreateUserModalProps) => {
  const [dialogState, setDialogState] = useAtom(userDialogStateAtom);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"user" | "admin" | "owner">("user");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { editorTheme } = useHandleAppTheme();

  useEffect(() => {
    if (dialogState.isOpen && dialogState.mode === "add") {
      // Generate a random username when dialog opens
      generateUsername();
    }
  }, [dialogState.isOpen, dialogState.mode]);

  const generateUsername = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/generate-username`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUsername(data.username);
      }
    } catch (err) {
      console.error("Failed to generate username:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          username,
          email,
          role,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create user");
      }

      // Show QR code
      if (data.qrCode) {
        setQrCodeDataUrl(data.qrCode);
        setShowSuccess(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setDialogState({ isOpen: false, mode: "add", userId: null });
    setUsername("");
    setEmail("");
    setRole("user");
    setError(null);
    setQrCodeDataUrl(null);
    setShowSuccess(false);
    setIsLoading(false);
  };

  const handleDone = () => {
    onUserCreated();
    handleClose();
  };

  if (!dialogState.isOpen || dialogState.mode !== "add") {
    return null;
  }

  return (
    <div
      className={clsx("excalidraw excalidraw-app", `theme--${editorTheme}`)}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
    >
      <div className="CreateUserModal__backdrop" onClick={handleClose} />
      <div className="CreateUserModal__modal">
        <div className="CreateUserModal__header">
          <h2 className="CreateUserModal__title">
            {showSuccess ? "User Created Successfully" : "Create New User"}
          </h2>
          <button
            className="CreateUserModal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="CreateUserModal__content">
          {showSuccess ? (
            <div className="CreateUserModal__success">
              <div className="CreateUserModal__success-message">
                <span className="CreateUserModal__success-icon">✓</span>
                <p>
                  User <strong>{username}</strong> has been created
                  successfully!
                </p>
              </div>

              {qrCodeDataUrl && (
                <div className="CreateUserModal__qr-section">
                  <h3>Scan QR Code to Setup MFA</h3>
                  <p className="CreateUserModal__info">
                    The user needs to scan this QR code with their authenticator
                    app (Google Authenticator, Authy, etc.)
                  </p>
                  <div className="CreateUserModal__qr-code">
                    <img src={qrCodeDataUrl} alt="QR Code for MFA setup" />
                  </div>
                  <div className="CreateUserModal__instructions">
                    <h4>Send these details to the user:</h4>
                    <ul>
                      <li>
                        <strong>Username:</strong> {username}
                      </li>
                      <li>
                        <strong>Login URL:</strong> {window.location.origin}
                      </li>
                      <li>Scan the QR code above with an authenticator app</li>
                      <li>Use the app to generate login codes</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="CreateUserModal__actions">
                <button
                  className="CreateUserModal__button CreateUserModal__button--primary"
                  onClick={handleDone}
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <div className="CreateUserModal__error">{error}</div>}

              <div className="CreateUserModal__field">
                <label htmlFor="username" className="CreateUserModal__label">
                  Username
                </label>
                <div className="CreateUserModal__input-group">
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="CreateUserModal__input"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="CreateUserModal__generate-btn"
                    onClick={generateUsername}
                    disabled={isLoading}
                    title="Generate random username"
                  >
                    🔄
                  </button>
                </div>
                <p className="CreateUserModal__hint">
                  A random username has been generated, but you can change it
                </p>
              </div>

              <div className="CreateUserModal__field">
                <label htmlFor="email" className="CreateUserModal__label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="CreateUserModal__input"
                  required
                  disabled={isLoading}
                />
                <p className="CreateUserModal__hint">
                  Used for account identification and recovery
                </p>
              </div>

              <div className="CreateUserModal__field">
                <label htmlFor="role" className="CreateUserModal__label">
                  Role
                </label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) =>
                    setRole(e.target.value as "user" | "admin" | "owner")
                  }
                  className="CreateUserModal__select"
                  disabled={isLoading}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
                <p className="CreateUserModal__hint">
                  Owner: Full access including user management
                  <br />
                  Admin: Can manage content but not users
                  <br />
                  User: Standard access
                </p>
              </div>

              <div className="CreateUserModal__actions">
                <button
                  type="button"
                  className="CreateUserModal__button"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="CreateUserModal__button CreateUserModal__button--primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
